import { withOneTrust } from '@segment/analytics-consent-wrapper-onetrust'
import { AnalyticsBrowser } from '@segment/analytics-next'

import type { SegmentLoadOptions } from './types'

let analyticsInstance: AnalyticsBrowser | undefined

/**
 * Builds Segment load options for the current origin: cookie domain on suitsupply.com,
 * storage priority localStorage -> cookie -> memory, no initial pageview, retry queue enabled.
 * Only runs in browser; safe to call from initializeAnalytics.
 */
const getSegmentLoadOptions = (): SegmentLoadOptions => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
  const isSuitsupplyDomain =
    hostname === 'suitsupply.com' || hostname.endsWith('.suitsupply.com')

  return {
    initialPageview: false,
    retryQueue: true,
    cookie: isSuitsupplyDomain
      ? { domain: '.suitsupply.com', path: '/', sameSite: 'Lax', secure: true }
      : { path: '/', sameSite: 'Lax', secure: true },
    storage: {
      stores: ['localStorage', 'cookie', 'memory'],
    },
    user: {
      storage: { stores: ['localStorage', 'cookie', 'memory'] },
    },
    group: {
      storage: { stores: ['localStorage', 'cookie', 'memory'] },
    },
  }
}

/**
 * Loads the Segment transport with the provided write key. When gated, the
 * OneTrust consent wrapper makes Segment wait for OneTrust and block
 * non-consented destinations. Applies enhanced load options (cookie domain,
 * storage priority, retry queue, no initial pageview).
 * Called once per page by initializeAnalytics() — the host's job (FC-183 §3).
 * @param writeKey - The Segment write key from environment config
 * @param gateBehindOneTrust - Whether to use the OneTrust consent wrapper
 * @throws {Error} If the write key is not defined or empty
 */
export const loadTransport = (
  writeKey: string,
  gateBehindOneTrust: boolean,
): void => {
  if (!writeKey || writeKey.trim() === '') {
    throw new Error('Segment write key is required but not set or empty')
  }

  if (analyticsInstance) {
    return
  }

  const browser = new AnalyticsBrowser()
  if (gateBehindOneTrust) {
    withOneTrust(browser).load({ writeKey }, getSegmentLoadOptions())
  } else {
    browser.load({ writeKey }, getSegmentLoadOptions())
  }
  analyticsInstance = browser
}

/**
 * Gets the analytics instance. Throws if not initialized.
 */
const getAnalytics = (): AnalyticsBrowser => {
  if (!analyticsInstance) {
    throw new Error(
      'Analytics has not been initialized. Call initializeAnalytics() first.',
    )
  }
  return analyticsInstance
}

/**
 * Exported analytics instance.
 * Uses a Proxy to defer initialization until initializeAnalytics() is called.
 */
export const analytics = new Proxy({} as AnalyticsBrowser, {
  get(_target, prop) {
    const instance = getAnalytics()
    const value = instance[prop as keyof AnalyticsBrowser]
    if (typeof value === 'function') {
      return value.bind(instance)
    }
    return value
  },
})
